import React, { useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import Cookies from "js-cookie";
import { useLocation, useParams } from "react-router-dom";

import AssessmentForm from "@/components/MealPlanForm/AssessmentForm/AssessmentForm";
import Header from "@/components/MealPlanForm/Header";
import MealPlanFormContainer from "@/components/MealPlanForm/MealPlanFormContainer";
import MealPlannerForm from "@/components/MealPlanForm/MealPlannerForm/MealPlannerForm";
import PlanningForm from "@/components/MealPlanForm/PlanningForm/PlanningForm";
import Step from "@/components/MealPlanForm/Step";
import MealPlanFormStore from "@/components/MealPlanForm/store/MealPlanFormStore";
import MealPlannerSurvey from "@/components/MealPlannerSurvey";
import { graphql } from "@/gql";
import useAlert from "@/hooks/useAlert";
import useClientAllergies from "@/hooks/useClientAllergies";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODate, parseDate } from "@/utils/datetime";
import { displayError, parseInvalidError } from "@/utils/errors";

import ProgramForm from "../components/ProgramForm";
import ProgramDetailsForm, {
  ProgramDetailsFormProps,
} from "../components/ProgramForm/ProgramDetailsForm";

const editPlanPageQuery = graphql(`
  query EditPlanPage($id: ID!) {
    program(id: $id) {
      id
      name
      description
      startDate
      endDate
    }
  }
`);

const updateProgramDetailsMutation = graphql(`
  mutation UpdateProgramDetails2($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      program {
        id
        name
        description
        startDate
        endDate
        client {
          id
          status
        }
      }
    }
  }
`);

const saveMealPlanMutation = graphql(`
  mutation SaveMealPlan2($input: UpdateProgramInput!) {
    updateProgram(input: $input) {
      program {
        id
        client {
          id
          status
        }
      }
    }
  }
`);

const EditPlanPage: React.FC = () => {
  const { planId, clientId } =
    useParams<{
      planId: string;
      clientId: string;
    }>();
  const snackbar = useGlobalSnackbar();
  const alert = useAlert();

  const { data } = useQuery(editPlanPageQuery, {
    variables: { id: planId ?? "" },
  });
  const [updateProgramDetails] = useMutation(updateProgramDetailsMutation);
  const [saveMealPlan] = useMutation(saveMealPlanMutation);
  const feedbackDialog = useDialogState();
  const clientAllergies = useClientAllergies(clientId ?? "");

  const [store] = useState(new MealPlanFormStore("program"));
  useEffect(() => {
    store.loadProgram(planId ?? "");
  }, [planId]);
  useEffect(() => {
    store.setClientAllergies(clientAllergies);
  }, [clientAllergies]);

  const { state } = useLocation();
  useEffect(() => {
    if (state?.fromDuplicateRecipe) store.setCurrentStep(state?.step);
  }, [state?.fromDuplicateRecipe]);

  const defaultDetailsValues = useMemo(() => {
    return data?.program
      ? {
          name: data?.program.name,
          description: data.program.description ?? undefined,
          startDate: parseDate(data.program.startDate),
          endDate: parseDate(data.program.endDate),
        }
      : undefined;
  }, [data?.program]);

  const handleDetailsSubmit: ProgramDetailsFormProps["onSubmit"] = (values) => {
    return updateProgramDetails({
      variables: {
        input: {
          id: planId ?? "",
          patch: {
            name: values.name,
            description: values.description,
            startDate: formatISODate(values.startDate),
            endDate: formatISODate(values.endDate),
          },
        },
      },
    })
      .then(() => {
        snackbar.open("Program details saved successfully");
        return store.loadProgram(planId ?? "");
      })
      .catch((e) => {
        console.error(JSON.stringify(e, null, 2));
        const invalidError = parseInvalidError(e);
        if (invalidError) {
          snackbar.open(invalidError.extensions.errors[0].fullMessage);
        } else {
          snackbar.open(displayError(e, "Unable to save program details"));
        }
      });
  };

  function save() {
    return saveMealPlan({
      variables: {
        input: {
          id: planId ?? "",
          patch: {
            mealPlanAttributes: store.toAttributes(),
          },
        },
      },
    });
  }

  function handleAssessmentSubmit() {
    // Optimistically, go to the next step.
    store.nextStep();
    save().catch((e) => {
      snackbar.open(displayError(e, "Unable to save meal plan"));
      // On error, go back to this step.
      store.setCurrentStep(0);
    });
  }

  function handlePlanningSubmit() {
    // Optimistically, go to the next step.
    store.nextStep();
    save().catch((e) => {
      snackbar.open(displayError(e, "Unable to save meal plan"));
      // On error, go back to this step.
      store.setCurrentStep(1);
    });
  }

  function handleAutoSave() {
    save().catch((e) => {
      snackbar.open(displayError(e, "Unable to save meal plan"));
    });
  }

  function handleManualSave() {
    save()
      .then(() => {
        alert.open("Meal plan saved successfully");
        if (!Cookies.get("mealPlanFeedbackDialogShown")) {
          Cookies.set("mealPlanFeedbackDialogShown", "true", { secure: true });
          feedbackDialog.show();
        }
      })
      .catch((e) => {
        console.error(JSON.stringify(e, null, 2));
        const invalidError = parseInvalidError(e);
        if (invalidError) {
          snackbar.open(invalidError.extensions.errors[0].fullMessage);
        } else {
          snackbar.open(displayError(e));
        }
        return Promise.reject(e);
      });
  }

  return (
    <>
      <ProgramForm
        backTo={`/clients/${clientId}/programs`}
        hasDetails={!!data?.program}
        renderProgramDetails={() => (
          <ProgramDetailsForm
            defaultValues={defaultDetailsValues}
            onSubmit={handleDetailsSubmit}
          />
        )}
        renderMealPlan={() => (
          <MealPlanFormContainer
            store={store}
            header={
              <Header
                title="Meal plan"
                steps={["Assessment", "Planning", "Meal plan"]}
              />
            }
          >
            <Step index={0}>
              <AssessmentForm onSubmit={handleAssessmentSubmit} />
            </Step>
            <Step index={1}>
              <PlanningForm onSubmit={handlePlanningSubmit} />
            </Step>
            <Step index={2}>
              <MealPlannerForm
                backButtonTo={`/clients/${clientId}/programs`}
                backButtonLabel="Back to programs"
                onAutoSave={handleAutoSave}
                onManualSave={handleManualSave}
              />
            </Step>
          </MealPlanFormContainer>
        )}
      />
      <MealPlannerSurvey
        visibility={feedbackDialog.shown}
        closeDialog={feedbackDialog.hide}
      />
    </>
  );
};

export default EditPlanPage;
